<template>
  <div class="product-info-wrap">
    <div class="product-title">
      <Icon class="product-title-icon" name="history-query" />
      <div class="product-title-text">
        <van-notice-bar
          class="title-notice"
          :delay="0.3"
          :scrollable="scrollable"
          :text="productName"
        />
        <span v-show="item._recentBuy" class="recent-buy">
          {{ letter.v3RecentBug }}
        </span>
      </div>
    </div>
    <div v-if="!hideProductContent" class="product-content">
      <van-notice-bar
        :delay="0.3"
        :scrollable="scrollable"
        :text="productDetail"
      />
    </div>
    <div class="product-btn-wrap">
      <div class="info" :style="infoStyle">
        <p class="currency">{{ currency || item.GAME_CURRENCY }}</p>
        <p class="price">{{ price }}</p>
      </div>
      <div
        class="cart-btn flex-center"
        :class="{ disabled: disabled }"
        @click.stop="handleCartAction"
      >
        <Icon class="cart-icon" name="shopping-cart-add" />
        <Icon
          v-if="showFlyIcon"
          class="fly-cart-icon"
          name="shopping-cart-add"
          :style="flyIconStyle"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import cartAddAndDetail from '@mixins/officialTopupNew/cartAddAndDetail';

export default {
  name: 'ProductInfo',
  mixins: [cartAddAndDetail],
  props: {
    item: {
      type: Object,
      default: () => {}
    },
    productName: {
      type: String,
      default: ''
    },
    productDetail: {
      type: String,
      default: ''
    },
    price: {
      type: String,
      default: ''
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showFlyIcon: false,
      flyIconStyle: {}
    };
  },
  computed: {
    ...mapState({
      gameId: state => state.gameId,
      letter: state => state.letter,
      currency: state => state.showCurrency,
      colorConfig: state => state.page.colorConfig,
      openShoppingCart: state => state.page.openShoppingCart,
      isLogin: state => state.login.isLogin
    }),
    ...mapGetters(['gameInfoConfig']),
    infoStyle() {
      return this.colorConfig?.priceColor
        ? `color: ${this.colorConfig?.priceColor}`
        : '';
    },
    hideProductContent() {
      return this.gameInfoConfig?.hideProductContent;
    }
  },
  methods: {
    handleCartAction() {
      if (this.disabled) {
        return;
      }
      this.openShoppingCart ? this.clickAddToCart() : this.selectProduct();
    },
    selectProduct() {
      this.isLogin
        ? this.$emit('selectProduct', this.item)
        : this.$emit('showLoginPopup');
    },
    clickAddToCart() {
      this.isLogin
        ? this.$emit('clickAddToCart', this.item, this.animateAddToCart)
        : this.$emit('showLoginPopup');
    },
    animateAddToCart() {
      // 获取目标购物车元素
      const targetCart = document.querySelector('.cart-info-icon-main');
      if (!targetCart) return;

      // 获取当前按钮位置
      const cartBtn = this.$el.querySelector('.cart-btn');
      const startRect = cartBtn.getBoundingClientRect();

      // 获取目标购物车位置
      const endRect = targetCart.getBoundingClientRect();

      // 获取页面缩放比例
      const computedStyle = window.getComputedStyle(document.body);
      const zoomFactor = parseFloat(computedStyle.zoom) || 1;

      // 计算开始和结束位置（精确到中心点）
      const startX = 0;
      const startY = 0;

      // 计算目标购物车的中心点，考虑zoom因素
      const endX =
        (endRect.left -
          startRect.left +
          endRect.width / 2 -
          (this.$el.querySelector('.fly-cart-icon')?.offsetWidth || 53) / 2) /
        zoomFactor;
      const endY =
        (endRect.top -
          startRect.top +
          endRect.height / 2 -
          (this.$el.querySelector('.fly-cart-icon')?.offsetHeight || 53) / 2) /
        zoomFactor;

      // 创建一个动画完成的标志
      let animationCompleted = false;

      // 设置动画参数
      const duration = {
        up: 200, // 上升阶段时间(ms)
        down: 400 // 下降阶段时间(ms)
      };
      const upDistance = Math.min(80, Math.abs(endY) * 0.2) / zoomFactor; // 向上弹起的高度
      const midX = startX + endX * 0.2; // 中间点X坐标

      // 显示飞行图标
      this.showFlyIcon = true;
      this.flyIconStyle = {
        position: 'absolute',
        left: `${startX}px`,
        top: `${startY}px`,
        zIndex: 100,
        willChange: 'transform' // 提示浏览器优化动画性能
      };

      // 第一阶段：向上弹起
      const startUpTime = performance.now();

      const animateUp = currentTime => {
        const elapsedTime = currentTime - startUpTime;
        const progress = Math.min(elapsedTime / duration.up, 1);

        // 使用easeOutBack曲线
        const easeOutBack = x => {
          const c1 = 1.70158;
          const c3 = c1 + 1;
          return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
        };

        const currentX = startX + (midX - startX) * progress;
        const currentY = startY - upDistance * easeOutBack(progress);

        this.flyIconStyle = {
          ...this.flyIconStyle,
          transform: `translate3d(${currentX}px, ${currentY}px, 0) scale(0.8)`,
          opacity: 1
        };

        if (progress < 1) {
          requestAnimationFrame(animateUp);
        } else {
          // 第一阶段结束，开始第二阶段
          const startDownTime = performance.now();
          animateDown(startDownTime, currentX, currentY);
        }
      };

      // 第二阶段：掉落到购物车
      const animateDown = (startDownTime, fromX, fromY) => {
        const animateFrame = currentTime => {
          const elapsedTime = currentTime - startDownTime;
          const progress = Math.min(elapsedTime / duration.down, 1);

          // 使用easeIn曲线模拟重力加速
          const easeIn = x => x * x;

          const currentX = fromX + (endX - fromX) * easeIn(progress);
          const currentY = fromY + (endY - fromY) * easeIn(progress);

          this.flyIconStyle = {
            ...this.flyIconStyle,
            transform: `translate3d(${currentX}px, ${currentY}px, 0) scale(${
              0.8 - 0.3 * progress
            })`,
            opacity: 1 - 0.2 * progress
          };

          if (progress < 1) {
            requestAnimationFrame(animateFrame);
          } else {
            // 动画完成
            this.showFlyIcon = false;
            animationCompleted = true;
          }
        };

        requestAnimationFrame(animateFrame);
      };

      // 开始动画
      requestAnimationFrame(animateUp);

      // 安全措施：如果动画没有正常完成，确保图标最终会被隐藏
      setTimeout(() => {
        if (!animationCompleted) {
          this.showFlyIcon = false;
        }
      }, duration.up + duration.down + 100);
    }
  }
};
</script>

<style scoped lang="scss">
@import '@assets/scss/officialTopupNew/officialTopupNew.scss';

.product-info-wrap {
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 20px;
  background-color: var(--commodityInfoBgColor);
  border-radius: 0 0 10px 10px;

  .product-title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    width: 100%;
    font-size: 20px;
    line-height: 1.1;
    color: $commodityNameColor;
    cursor: pointer;
    &-text {
      flex: 1;
      min-width: 0; // 允许子元素在flex容器内正确收缩
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: nowrap;

      .recent-buy {
        display: inline-block;
        margin-left: 10px;
        padding: 4px;
        border: 1px solid red;
        border-radius: 4px;
        color: red;
        text-align: center;
        white-space: nowrap;
      }

      // 让标题的公告条在flex布局中可收缩且占满剩余宽度
      ::v-deep .title-notice {
        flex: 1;
        min-width: 0;
        padding: 0;
        background-color: transparent;
        font-size: 20px;
        color: $commodityNameColor;
      }
    }

    .product-title-icon {
      flex-shrink: 0;
      display: inline-block;
      width: 24px;
      height: 24px;
      margin-right: 10px;
      color: $commodityNameColor;
    }
  }
  .product-content {
    margin-top: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 20px;
    color: $commodityDescColor;

    ::v-deep .van-notice-bar {
      height: 24px;
      padding: 0;
      background-color: transparent;
      font-size: 18px;
      color: $commodityDescColor;
    }
  }
  .product-btn-wrap {
    position: relative;
    margin-top: 30px;
    width: 100%;
    height: 60px;
    border-radius: 9px;
    line-height: 1.1;

    .info {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-weight: bold;
      .currency {
        margin-bottom: 2px;
        font-size: 20px;
      }
      .price {
        font-size: 46px;
        font-family: 'BAHNSCHRIFT';
      }
    }
    .cart-btn {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 60px;
      height: 60px;
      margin: auto;
      text-align: center;
      height: 100%;
      border-radius: 10px;
      background: linear-gradient(
        348deg,
        $buttonGradientColor1,
        $buttonGradientColor2
      );
      font-size: 22px;
      color: #ffffff;
      cursor: pointer;
      overflow: visible;

      &.disabled {
        filter: grayscale(100%);
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      .cart-icon {
        width: 37px;
        height: 37px;
        color: #ffffff;
      }

      .fly-cart-icon {
        position: absolute;
        width: 37px;
        height: 37px;
        color: #ffffff;
        z-index: 100;
        background: linear-gradient(
          348deg,
          $buttonGradientColor1,
          $buttonGradientColor2
        );
        border-radius: 50%;
        padding: 8px;
        box-sizing: content-box;
      }
    }
  }
}
#ssr-page.landscape {
  .product-info-wrap {
    padding-bottom: 10px;

    .product-title {
      font-size: 16px;
      .product-title-icon {
        width: 23px;
        height: 23px;
      }
      .recent-buy {
        font-size: 16px;
      }
      ::v-deep .title-notice {
        font-size: 16px;
      }
    }
    .product-content {
      font-size: 16px;

      ::v-deep .van-notice-bar {
        height: 20px;
        font-size: 16px;
      }
    }
    .product-btn-wrap {
      margin-top: 16px;
      .info {
        .currency {
          margin-bottom: 0;
          font-size: 16px;
        }

        .price {
          font-size: 36px;
        }
      }

      .cart-btn {
        width: 46px;
        height: 46px;
        .cart-icon {
          width: 32px;
          height: 32px;
        }

        .fly-cart-icon {
          width: 32px;
          height: 32px;
          padding: 7px;
        }
      }
    }
  }
}
</style>
